import { useEffect, useState } from 'react';
import { Button, Group, Modal, Stack, Text, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { DateInput, TimeInput } from '@mantine/dates';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { uzDateProps } from '../lib/dateInput';

export interface StaffLogTarget {
  user: { id: string; fullName: string };
  /** "YYYY-MM-DD" — qaysi kun uchun. */
  sana: string;
  /** Mavjud yozuv — tahrirlash; `null` — yangi yozuv. */
  log: { id: string; checkIn: string; checkOut?: string | null } | null;
}

const kalit = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** Instant → "HH:MM" (brauzer mahalliy vaqti = Toshkent). */
const hhmm = (v?: string | null) =>
  v ? new Date(v).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '';
/** Sana + "HH:MM" → Toshkent vaqtidagi aniq instant (server UTC da ishlaydi). */
const instant = (sana: string, vaqt: string) => `${sana}T${vaqt}:00+05:00`;

/**
 * Xodim davomatini QO'LDA, SANA VA VAQT bilan belgilash / tahrirlash.
 *
 * Tezkor "Keldi"/"Ketdi" tugmalari HOZIRGI vaqtni yozadi — bu faqat bugun, xodim
 * ko'z oldida kelganda to'g'ri. Terminal ishlamagan o'tgan kunni to'ldirish yoki
 * noto'g'ri yozilgan vaqtni tuzatish uchun aniq vaqt kerak: shu oyna.
 * Kunlik ro'yxatdan ham, oylik jadval katagidan ham ochiladi. Saqlash/o'chirishdan keyin maosh
 * hisoblari ham yangilanadi (bu yerda — ikkala chaqiruvchi uchun bir joyda).
 */
export function StaffLogModal({
  target,
  onClose,
  onSaved,
}: {
  target: StaffLogTarget | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [sana, setSana] = useState<Date | null>(null);
  const [keldi, setKeldi] = useState('');
  const [ketdi, setKetdi] = useState('');
  const theme = useMantineTheme();
  // Telefonda oyna butun ekranni egallaydi — klaviatura ochilganda maydonlar yopilib qolmasin
  const telefon = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  useEffect(() => {
    if (!target) return;
    const [y, m, d] = target.sana.split('-').map(Number);
    setSana(new Date(y, m - 1, d));
    setKeldi(target.log ? hhmm(target.log.checkIn) : '08:00');
    setKetdi(target.log ? hhmm(target.log.checkOut) : '');
  }, [target]);

  const xato = (e: unknown) =>
    notifications.show({
      color: 'red',
      message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error'),
    });
  const tayyor = (msg: string) => {
    notifications.show({ color: 'green', message: msg });
    // Kelish-ketish maoshga ta'sir qiladi — xodim kartasi, Xodimlar ro'yxati va Maosh sahifasi ham
    for (const key of ['salary-compute', 'payroll-summary', 'payroll-oylik']) qc.invalidateQueries({ queryKey: [key] });
    onSaved();
    onClose();
  };

  const saqla = useMutation({
    mutationFn: async () => {
      const kun = kalit(sana!);
      const checkIn = instant(kun, keldi);
      const checkOut = ketdi ? instant(kun, ketdi) : null;
      if (target!.log) {
        return api.patch(`/payroll/logs/${target!.log.id}`, { checkIn, checkOut });
      }
      return api.post('/payroll/logs/check-in', {
        userId: target!.user.id,
        checkIn,
        ...(checkOut ? { checkOut } : {}),
        note: t('staffAttendance.manual'),
      });
    },
    onSuccess: () => tayyor(t('staffAttendance.saved')),
    onError: xato,
  });

  const ochir = useMutation({
    mutationFn: () => api.delete(`/payroll/logs/${target!.log!.id}`),
    onSuccess: () => tayyor(t('staffAttendance.deleted')),
    onError: xato,
  });
  const [tasdiq, setTasdiq] = useState(false);
  useEffect(() => setTasdiq(false), [target]);

  // Ketish kelishdan oldin bo'lsa — saqlash tugmasi o'chadi va sabab ko'rsatiladi
  const tartibXato = !!ketdi && !!keldi && ketdi <= keldi;

  return (
    <Modal
      opened={!!target}
      onClose={onClose}
      title={target?.log ? t('staffAttendance.editTitle') : t('staffAttendance.addTitle')}
      centered
      fullScreen={!!telefon}
    >
      {target && (
        <Stack gap="sm">
          <Text fw={600}>{target.user.fullName}</Text>
          <DateInput
            label={t('staffAttendance.date')}
            value={sana}
            onChange={setSana}
            maxDate={new Date()}
            {...uzDateProps}
            clearable={false}
          />
          <Group grow>
            <TimeInput
              label={t('staffAttendance.in')}
              value={keldi}
              onChange={(e) => setKeldi(e.currentTarget.value)}
              required
            />
            <TimeInput
              label={t('staffAttendance.out')}
              value={ketdi}
              onChange={(e) => setKetdi(e.currentTarget.value)}
              error={tartibXato ? t('staffAttendance.outBeforeIn') : undefined}
            />
          </Group>
          <Text size="xs" c="dimmed">{t('staffAttendance.outHint')}</Text>
          <Group justify="space-between" mt="sm">
            {target.log ? (
              <Button
                variant="subtle"
                color="red"
                loading={ochir.isPending}
                onClick={() => (tasdiq ? ochir.mutate() : setTasdiq(true))}
              >
                {tasdiq ? t('staffAttendance.deleteConfirm') : t('staffAttendance.delete')}
              </Button>
            ) : <span />}
            <Group gap="xs">
              <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
              <Button
                loading={saqla.isPending}
                disabled={!sana || !keldi || tartibXato}
                onClick={() => saqla.mutate()}
              >
                {t('common.save')}
              </Button>
            </Group>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
